import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Filter,
  Tag,
  Package,
  ChevronDown,
  X,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Checkbox } from './checkbox';
import { GroupingType, GroupingOptions } from '../../utils/procurementGroupingUtils';

interface ProcurementGroupingControlsProps {
  options: GroupingOptions;
  availableCategories: string[];
  availableBrands: string[];
  onOptionsChange: (newOptions: Partial<GroupingOptions>) => void;
}

export function ProcurementGroupingControls({
  options,
  availableCategories,
  availableBrands,
  onOptionsChange
}: ProcurementGroupingControlsProps) {

  const handleGroupByChange = (value: GroupingType) => {
    onOptionsChange({ groupBy: value });
  };

  const handleCategoryToggle = (category: string) => {
    const isSelected = options.selectedCategories.includes(category);
    const newCategories = isSelected
      ? options.selectedCategories.filter(c => c !== category)
      : [...options.selectedCategories, category];
    
    onOptionsChange({ selectedCategories: newCategories });
  };

  const handleBrandToggle = (brand: string) => {
    const isSelected = options.selectedBrands.includes(brand);
    const newBrands = isSelected
      ? options.selectedBrands.filter(b => b !== brand)
      : [...options.selectedBrands, brand];
    
    onOptionsChange({ selectedBrands: newBrands });
  };

  const handleClearAllCategories = () => {
    onOptionsChange({ selectedCategories: [] });
  };

  const handleClearAllBrands = () => {
    onOptionsChange({ selectedBrands: [] });
  };

  const handleSelectAllCategories = () => {
    onOptionsChange({ selectedCategories: [...availableCategories] });
  };

  const handleSelectAllBrands = () => {
    onOptionsChange({ selectedBrands: [...availableBrands] });
  };

  const getGroupingIcon = (groupBy: GroupingType) => {
    switch (groupBy) {
      case 'category':
        return <Tag className="w-4 h-4" />;
      case 'brand':
        return <Package className="w-4 h-4" />;
      default:
        return <Filter className="w-4 h-4" />;
    }
  };

  const hasActiveFilters = options.selectedCategories.length > 0 || options.selectedBrands.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6"
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-jamaica-green" />
            <h3 className="text-lg font-semibold text-gray-900">Filters & Grouping</h3>
          </div>
          {hasActiveFilters && (
            <Badge variant="secondary" className="bg-jamaica-green/10 text-jamaica-green">
              {options.selectedCategories.length + options.selectedBrands.length} active filters
            </Badge>
          )}
        </div>

        {/* Controls Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {/* Group By Control */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Group by</label>
            <Select value={options.groupBy} onValueChange={handleGroupByChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select grouping..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    No Grouping
                  </div>
                </SelectItem>
                <SelectItem value="category">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Category
                  </div>
                </SelectItem>
                <SelectItem value="brand">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Brand
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Categories</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  disabled={availableCategories.length === 0}
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    <span className="truncate">
                      {options.selectedCategories.length === 0
                        ? 'All categories'
                        : options.selectedCategories.length === 1
                        ? options.selectedCategories[0]
                        : `${options.selectedCategories.length} selected`
                      }
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full max-w-sm md:w-80 p-0" align="start">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm">Filter by Category</h4>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAllCategories}
                        className="text-xs h-6 px-2"
                      >
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAllCategories}
                        className="text-xs h-6 px-2"
                      >
                        None
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableCategories.map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={options.selectedCategories.includes(category)}
                          onCheckedChange={() => handleCategoryToggle(category)}
                        />
                        <label
                          htmlFor={`category-${category}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {category}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Brand Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Brands</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  disabled={availableBrands.length === 0}
                >
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    <span className="truncate">
                      {options.selectedBrands.length === 0
                        ? 'All brands'
                        : options.selectedBrands.length === 1
                        ? options.selectedBrands[0]
                        : `${options.selectedBrands.length} selected`
                      }
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full max-w-sm md:w-80 p-0" align="start">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm">Filter by Brand</h4>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAllBrands}
                        className="text-xs h-6 px-2"
                      >
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAllBrands}
                        className="text-xs h-6 px-2"
                      >
                        None
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableBrands.map((brand) => (
                      <div key={brand} className="flex items-center space-x-2">
                        <Checkbox
                          id={`brand-${brand}`}
                          checked={options.selectedBrands.includes(brand)}
                          onCheckedChange={() => handleBrandToggle(brand)}
                        />
                        <label
                          htmlFor={`brand-${brand}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {brand}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filter Chips */}
        <AnimatePresence>
          {hasActiveFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-wrap gap-2 md:gap-3"
            >
              {options.selectedCategories.map((category) => (
                <motion.div
                  key={`category-${category}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
                    onClick={() => handleCategoryToggle(category)}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {category}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                </motion.div>
              ))}
              {options.selectedBrands.map((brand) => (
                <motion.div
                  key={`brand-${brand}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Badge
                    variant="secondary"
                    className="bg-purple-100 text-purple-800 hover:bg-purple-200 cursor-pointer"
                    onClick={() => handleBrandToggle(brand)}
                  >
                    <Package className="w-3 h-3 mr-1" />
                    {brand}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Existing Filter Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOptionsChange({ showOnlyIncomplete: !options.showOnlyIncomplete })}
            className={`hover:bg-yellow-50 ${options.showOnlyIncomplete ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : ''}`}
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            {options.showOnlyIncomplete ? 'Show All' : 'Only Incomplete'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOptionsChange({ hideChecked: !options.hideChecked })}
            className="hover:bg-gray-50"
          >
            {options.hideChecked ? (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Collected
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Collected
              </>
            )}
          </Button>
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOptionsChange({ selectedCategories: [], selectedBrands: [] })}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}